// An Assessment as a card on the admin's Training & Assessment board, in its
// stage's column beside the Trainings (the learner's board shows it the same
// way: capability-setup/learner/AssessmentCards.tsx). It is a program card
// with nothing to edit, and it moves along the same line a Training does:
//
//   Design     still being built in Run Assessment (a draft the backend keeps:
//              capability-setup/create/assessmentDrafts.ts). Continue reopens
//              the flow where it was left, for those who may change it; the
//              team's other managers see the card without Continue.
//   Invite     run, but nobody holds the roles it measures yet: Design and
//              Publish are done, and the team has still to be invited.
//   The dial   run, and it has people: the share of them who have answered
//              every capability of their own role.
//
// Continue on a run Assessment opens what it asks, read-only.
//
// No React here, so it can be tested on its own (adminAssessmentCards.test.ts).

import type { AdminAssessmentView } from '../../../../../../api/capability';
import type { AssessmentDraft } from '../../../capability-setup/create/assessmentDrafts';
import type { ProgramCardData, ProgramStatus, ProgramTypeKey } from './types';
import { formatDate } from '../../../../../../utils/formatDate';

/** What the card says about its Assessment, where a program card would say something else. */
export interface AssessmentCardInfo {
  status: 'Design' | 'Published' | 'Running' | 'Scheduled' | 'Closed';
  /** "Started:" once its run is under way, "Starts:" while its first run is still ahead. */
  startLabel: 'Started' | 'Starts';
  startDate: string;
  /** What the dial counts, in words. */
  completion: string;
  /** Where it is on the Design, Publish, Invite line, in words, while it is not live. */
  stage: string;
}

export interface AdminAssessmentCard {
  /** A run Assessment, as the backend has it; null for one still being built. */
  assessment: AdminAssessmentView | null;
  /** One still being built: what has been entered so far. */
  draft: AssessmentDraft | null;
  card: ProgramCardData;
  info: AssessmentCardInfo;
  /** The teams of the roles it measures, where they are known: what the board's Teams filter goes by. */
  teamIds: string[];
}

const day = (iso: string) => formatDate(iso);

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

/** What every Assessment card is, whatever stage it is at. */
const cardOf = (fields: {
  id: string;
  name: string;
  stageId: string | null;
  category: string | null;
  status: ProgramStatus;
  completion: number;
  ownerName: string | null;
  roleIds: string[];
  capabilityIds: string[];
  pillarIds: string[];
}): ProgramCardData => ({
  id: fields.id,
  // Its own stage's column, or the first when that stage is gone.
  learningPillarId:
    fields.stageId && fields.pillarIds.includes(fields.stageId) ? fields.stageId : fields.pillarIds[0] ?? '',
  name: fields.name,
  programType: (fields.category as ProgramTypeKey | null) ?? 'role-excellence',
  model: 'masterclass',
  status: fields.status,
  audience: null,
  audienceRules: null,
  launchDate: null,
  programDueDate: null,
  programCompletionRate: fields.completion,
  owner: fields.ownerName ? { id: '', name: fields.ownerName } : null,
  creator: null,
  displayOrder: 0,
  locked: false,
  // Nothing on it is edited from the board.
  canEdit: false,
  roleIds: fields.roleIds,
  capabilityIds: fields.capabilityIds,
  format: null,
  guided: false
});

/**
 * Each run Assessment as a card. Completion is the people who have answered
 * every capability of their own role, out of the people who hold the roles it
 * measures (`audience`; never fewer than have started it, so the dial cannot
 * pass 100%). With nobody holding its roles it waits at Invite. A backend that
 * does not send `audience` yet leaves that unknown: the card is then counted
 * over those who have started, and never said to be waiting on a guess.
 */
export const adminAssessmentCards = (assessments: AdminAssessmentView[], pillarIds: string[]): AdminAssessmentCard[] =>
  assessments.map((assessment) => {
    const known = typeof assessment.audience === 'number';
    const started = assessment.run?.started ?? 0;
    const completed = assessment.run?.completed ?? 0;
    const audience = Math.max(assessment.audience ?? 0, started);
    const date = day(assessment.run?.startsAt ?? assessment.firstRun);
    const waiting = known && audience === 0 && assessment.status !== 'closed';
    const status: AssessmentCardInfo['status'] =
      assessment.status === 'closed' ? 'Closed' : waiting ? 'Published' : assessment.run ? 'Running' : 'Scheduled';
    return {
      assessment,
      draft: null,
      teamIds: assessment.teamIds ?? [],
      info: {
        status,
        startLabel: assessment.run ? 'Started' : 'Starts',
        startDate: date,
        completion:
          audience === 0
            ? 'Nobody has started this Assessment yet.'
            : `${completed} of the ${people(audience)} it is for ${
                completed === 1 ? 'has' : 'have'
              } answered every capability of their role. ${started} ${started === 1 ? 'has' : 'have'} started it.`,
        stage:
          'This Assessment is published, but nobody holds its roles yet. Invite the team in Capabilities and it reaches them.'
      },
      card: cardOf({
        id: assessment.id,
        // Each run is told apart by the date it started, as on the learner's card.
        name: `${assessment.name} | ${date}`,
        stageId: assessment.stageId,
        category: assessment.category,
        // Live (the dial) once it has people; until then it waits at Invite.
        status: waiting ? 'published' : 'active',
        completion: audience > 0 ? Math.min(100, Math.round((completed / audience) * 100)) : 0,
        ownerName: assessment.ownerName,
        roleIds: assessment.roleIds,
        capabilityIds: assessment.items.map((i) => i.capabilityId),
        pillarIds
      })
    };
  });

/** Each Assessment still being built as a card, at Design. */
export const draftAssessmentCards = (drafts: AssessmentDraft[], pillarIds: string[]): AdminAssessmentCard[] =>
  drafts.map((draft) => ({
    assessment: null,
    draft,
    teamIds: draft.teamIds,
    info: {
      status: 'Design',
      startLabel: 'Starts',
      startDate: draft.startOnPublish || !draft.firstRun ? 'On publish' : day(draft.firstRun),
      completion: '',
      stage: draft.canEdit
        ? 'This Assessment is still being built. Continue to finish it and run it.'
        : 'This Assessment is still being built. It measures a team you do not manage, so an org admin or a manager of all its teams finishes it.'
    },
    card: cardOf({
      id: draft.id,
      name: draft.details.name.trim() || 'Untitled Assessment',
      stageId: draft.details.stageId,
      category: draft.details.category,
      status: 'design',
      completion: 0,
      ownerName: draft.ownerName,
      roleIds: draft.details.roleIds,
      capabilityIds: draft.capabilityIds ?? [],
      pillarIds
    })
  }));

/** The cards the board shows: those measuring a selected team's role (all of them with no team selected). */
export const forTeams = (cards: AdminAssessmentCard[], selectedTeamIds: string[]): AdminAssessmentCard[] =>
  selectedTeamIds.length === 0
    ? cards
    : cards.filter((c) => c.teamIds.length === 0 || c.teamIds.some((id) => selectedTeamIds.includes(id)));
